import { useId, useState } from 'react'
import { useOutsideClick } from '../hooks/useOutsideClick'

// Dropdown of filters, mirroring ColumnsMenu: controlled open state so it works by
// keyboard and stays open while changing selections.
//
// Each option is a segmented three-way switch rather than a checkbox, so a filter can
// say "don't care" / "must be true" / "must be false" — a plain toggle can only express
// the first two. `values[key]` is the query value: '' (don't filter), 'true', or 'false'.
export default function FiltersMenu({ options, values, onChange, label = 'Filters' }) {
  const [open, setOpen] = useState(false)
  const ref = useOutsideClick(() => setOpen(false), open)
  const groupId = useId()
  // '' is the neutral choice, so anything else counts as an applied filter.
  const activeCount = options.filter(o => values[o.key]).length

  return (
    <div className={`dropdown ${open ? 'dropdown-open' : ''}`} ref={ref}>
      <button
        type="button"
        className={`btn btn-sm ${activeCount ? 'btn-primary' : 'btn-outline'}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {label}
        {activeCount > 0 && <span className="badge badge-xs shrink-0">{activeCount}</span>}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className="dropdown-content bg-base-200 rounded-box z-10 w-72 p-3 shadow-lg flex flex-col gap-4">
        {options.map(opt => (
          <div key={opt.key} className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{opt.label}</span>
            {opt.hint && <span className="text-xs opacity-50 leading-snug">{opt.hint}</span>}
            {/* Real radios: one choice at a time, arrow-key navigable, and the
                whole group is announced together. */}
            <div role="tablist" className="tabs tabs-box tabs-sm">
              {opt.choices.map(choice => (
                <input
                  key={choice.value}
                  type="radio"
                  role="tab"
                  className="tab"
                  name={`${groupId}-${opt.key}`}
                  aria-label={choice.label}
                  checked={(values[opt.key] ?? '') === choice.value}
                  onChange={() => onChange(opt.key, choice.value)}
                />
              ))}
            </div>
          </div>
        ))}

        {activeCount > 0 && (
          <button
            type="button"
            className="btn btn-xs btn-ghost justify-start"
            onClick={() => options.forEach(o => onChange(o.key, ''))}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  )
}
